import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../constants'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getAssetSummary } from '../../api/my'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, RowBetween, ColumnItem, CenterItem } from '../../components/common'
import { Button } from '../../components/button'
import { Card } from '../../components/card/Card'
import { Label } from '../../components/label'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { CheckVerificationModal, CreateAccountModal } from '../settings/modal'

import { ReactComponent as WalletIcon } from '../../assets/gui/investment/wallet_42.svg'
import { ReactComponent as CoinIcon } from '../../assets/gui/investment/coin_42.svg'
import { ReactComponent as RightLargeArrowIcon } from '../../assets/ico/common/arrow/right/arrow_right_black_large.svg'

const HEAD_INFO = {
  title: '투자 내역 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '투자 내역 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function MyPage() {
  const {
    commonStore: { setShowTab, setActiveTab },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const queryClient = useQueryClient()
  const { data: userInfo, isPending: isUserInfoPending, isError: isUserInfoError } = useUserInfo()
  const navigate = useNavigate()

  const {
    data: assetSummary,
    isPending,
    isError,
  } = useQuery({ queryKey: [QUERY_KEY.assetSummary], queryFn: getAssetSummary })

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })

    setShowTab(true)
    setActiveTab('myPage')
  }, [])

  if (isPending || isError || isUserInfoPending || isUserInfoError) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="투자 내역" isBack={false} />
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        </Wrap>
      </>
    )
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.assetSummary] })
  }

  const checkAccount = () => {
    if (!userInfo?.identification) {
      openModal({ id: MODALS_ID.CHECK_VERIFICATION, Component: CheckVerificationModal })
      return false
    }

    if (!userInfo?.withdrawalAccount || !userInfo?.depositAccount) {
      openModal({ id: MODALS_ID.CREATE_ACCOUNT, Component: CreateAccountModal })
      return false
    }

    return true
  }

  const goPurchaseProducts = () => {
    const isAccount = checkAccount()
    if (isAccount) navigate('/myPage/asset')
  }

  const goAccount = () => {
    const isAccount = checkAccount()
    if (isAccount) navigate('/myPage/account')
  }

  const checkWithdrawal = () => {
    if (checkAccount()) navigate('/myPage/account/withdrawal')
  }

  const checkDeposit = () => {
    if (checkAccount()) navigate('/myPage/account/deposit')
  }

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="투자 내역" isBack={false} />
        <StyledContent backgroundColor="gray_1" isPullEvent={true} onPull={refresh}>
          <ColumnItem $gap={16}>
            <Card $direction="column" $gap={16} $padding="24px" $radius={16} $background="white">
              <NavigateRow onClick={goPurchaseProducts}>
                <AlignCenterItem $gap={16}>
                  <WalletIcon />
                  <ColumnItem $gap={4}>
                    <Text size={16}>총 보유 자산</Text>
                    <Text size={24} weight={600} color="primary_5">
                      {assetSummary.totalBalance.toLocaleString()}원
                    </Text>
                  </ColumnItem>
                </AlignCenterItem>
                <RightLargeArrowIcon />
              </NavigateRow>
              <DivideLine />
              <RowBetween>
                <Text size={16}>청약 신청수량</Text>
                <Text size={16} weight={700} color="primary_5">
                  {assetSummary.subscriptionShares.toLocaleString()}주
                </Text>
              </RowBetween>
              <RowBetween>
                <Text size={16}>배정수량</Text>
                <Text size={16} weight={700} color="primary_5">
                  {assetSummary.allotmentShares.toLocaleString()}주
                </Text>
              </RowBetween>
              <RowBetween>
                <Text size={16}>총 보유 작품 수량</Text>
                <Text size={16} weight={700} color="primary_5">
                  {assetSummary.productCount.toLocaleString()}개
                </Text>
              </RowBetween>
            </Card>
            <Card $direction="column" $gap={16} $padding="24px" $radius={16} $background="white">
              <NavigateRow onClick={goAccount}>
                <AlignCenterItem $gap={16}>
                  <CoinIcon />
                  <ColumnItem $gap={4}>
                    <Text size={16}>예치금</Text>
                    <AlignCenterItem $gap={8}>
                      <Text size={24} weight={600} color="primary_5">
                        {assetSummary.depositBalance.toLocaleString()}원
                      </Text>
                      {!assetSummary.depositBalance && (
                        <Label size="small" backgroundColor="primary_2" textColor="primary_5">
                          예치금을 충전해주세요
                        </Label>
                      )}
                    </AlignCenterItem>
                  </ColumnItem>
                </AlignCenterItem>
                <RightLargeArrowIcon />
              </NavigateRow>
              <DivideLine />
              <RowBetween>
                <Text size={16}>청약 가능 금액</Text>
                <Text size={16} weight={700} color="primary_5">
                  {assetSummary.depositBalance.toLocaleString()}원
                </Text>
              </RowBetween>
              <RowBetween>
                <Text size={16}>출금 가능 금액</Text>
                <Text size={16} weight={700} color="primary_5">
                  {assetSummary.withdrawableBalance.toLocaleString()}원
                </Text>
              </RowBetween>
              <ButtonBox>
                <Button type="secondary" size="small" block={true} onClick={checkWithdrawal}>
                  출금하기
                </Button>
                <Button type="primary" size="small" block={true} onClick={checkDeposit}>
                  충전하기
                </Button>
              </ButtonBox>
            </Card>
          </ColumnItem>
        </StyledContent>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  padding: 16px;
`

const NavigateRow = styled(RowBetween)`
  cursor: pointer;
`

const LoadingContainer = styled(CenterItem)`
  width: 100%;
  height: 100%;
`

const ButtonBox = styled(RowBetween)`
  gap: 8px;
  margin-top: 16px;
`
